import { uuidV7 } from './uuid'

/**
 * Client HTTP de l'application. Même origine, cookie de session posé par le serveur :
 * il n'y a donc aucun jeton à porter ni à stocker côté client (décision B3).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors: Readonly<Record<string, readonly string[]>> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type ProblemDetails = {
  readonly title?: string
  readonly detail?: string
  readonly errors?: Record<string, string[]>
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorMessage(response), await readFieldErrors(response))
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

/**
 * La rédaction du cahier impose qu'une erreur « dise ce qui s'est passé et quoi faire ».
 * On préfère donc toujours le message du serveur au message générique du navigateur.
 */
async function readErrorMessage(response: Response): Promise<string> {
  const problem = await readProblem(response)
  const firstFieldError = problem?.errors && Object.values(problem.errors)[0]?.[0]

  return (
    firstFieldError ??
    problem?.detail ??
    problem?.title ??
    "La connexion au service a échoué. Réessayez dans un instant."
  )
}

async function readFieldErrors(response: Response): Promise<Record<string, string[]>> {
  return (await readProblem(response))?.errors ?? {}
}

const problemCache = new WeakMap<Response, ProblemDetails | null>()

async function readProblem(response: Response): Promise<ProblemDetails | null> {
  if (problemCache.has(response)) {
    return problemCache.get(response) ?? null
  }

  let problem: ProblemDetails | null = null
  try {
    problem = (await response.clone().json()) as ProblemDetails
  } catch {
    problem = null
  }

  problemCache.set(response, problem)
  return problem
}

export type SpaceSummary = {
  readonly id: string
  readonly name: string
  readonly emblemInitials: string
  readonly emblemHue: number
  readonly memberCount: number
  readonly itemCount: number
  readonly unseenCount: number
  readonly isActive: boolean
}

export type Me = {
  readonly id: string
  readonly email: string
  readonly displayName: string
  readonly activeSpaceId: string | null
  readonly isAdmin: boolean
  /** Décide si l'écran propose « définir » ou « changer » le mot de passe. */
  readonly hasPassword: boolean
  /**
   * Le mot de passe actuel vient d'un tiers — administrateur, courrier — et doit être remplacé
   * avant toute autre chose.
   */
  readonly mustChangePassword: boolean
  /**
   * Un code confidentiel existe déjà. Faux : poser un verrou passe d'abord par l'écran qui le
   * crée — autrement le bouton refuserait sans dire quoi faire.
   */
  readonly hasPin: boolean
  readonly spaces: readonly SpaceSummary[]
}

export type CreatedUser = {
  readonly user: AdminUser
  /** Rendu une seule fois : le mot de passe n'est stocké que haché. */
  readonly generatedPassword: string | null
  /** Renseigné quand le courrier n'est pas parti — il faut alors le transmettre soi-même. */
  readonly mailNotice: string | null
}

export type AdminUserSpace = {
  readonly id: string
  readonly name: string
  readonly emblemInitials: string
  readonly emblemHue: number
  readonly role: string
}

export type ImportedMessage = {
  readonly id: string
  readonly alias: string | null
  readonly sender: string | null
  readonly subject: string | null
  readonly spaceName: string | null
  readonly itemId: string | null
  /** Nul quand le message a été accepté ; sinon la raison du refus, en clair. */
  readonly rejectedReason: string | null
  /** Aperçu du texte, conservé pour les seuls messages écartés. */
  readonly bodyText: string | null
  readonly mailDeleted: boolean
  readonly processedAt: string
}

export type IngestionReport = {
  readonly read: number
  readonly imported: number
  readonly rejected: number
  readonly error: string | null
}

export type MailSettings = {
  readonly address: string | null
  readonly senderName: string | null
  readonly sendEnabled: boolean
  readonly receiveEnabled: boolean
  /** Minutes entre deux relèves de la boîte. */
  readonly pollMinutes: number
  /** Le mot de passe lui-même n'est jamais renvoyé : seule son existence l'est. */
  readonly hasPassword: boolean
  readonly updatedAt: string | null
}

export type TestMailResult = {
  readonly sent: boolean
  readonly message: string
}

export type AdminUser = {
  readonly id: string
  readonly email: string
  readonly displayName: string
  readonly username: string | null
  readonly isAdmin: boolean
  readonly hasPassword: boolean
  readonly spaceCount: number
  readonly spaces: readonly AdminUserSpace[]
  readonly createdAt: string
  readonly lastSeenAt: string | null
}

export type AdminSpace = {
  readonly id: string
  readonly name: string
  readonly emblemInitials: string
  readonly emblemHue: number
  /** Ce qui suit le « + » dans l'adresse de dépôt. Nul tant qu'aucun alias n'est attribué. */
  readonly emailAlias: string | null
  readonly memberCount: number
  readonly itemCount: number
  readonly createdAt: string
  /** Qui possède l'espace : le membre qui en porte le rôle, à défaut celui qui l'a créé. */
  readonly ownerName: string | null
}

export type AdminMember = {
  readonly userId: string
  readonly email: string
  readonly displayName: string
  readonly role: string
}

export type AdminSpaceDetail = {
  readonly space: AdminSpace
  readonly members: readonly AdminMember[]
}

export type CategoryDisplayMode = 'cards' | 'list' | 'checklist'

export type Category = {
  readonly id: string
  readonly name: string
  readonly initials: string
  readonly hue: number
  readonly itemCount: number
  /** Présentation des fiches : mosaïque, rangées, ou rangées à cocher. */
  readonly displayMode: CategoryDisplayMode
  /** Actions proposées sur les fiches de cette catégorie. */
  readonly showIngredients: boolean
  readonly showGift: boolean
  /** Une fiche de cette catégorie peut porter une dépense. */
  readonly showExpense: boolean
}

export type SpaceDetail = {
  readonly space: SpaceSummary
  readonly categories: readonly Category[]
  /** Archivées : invisibles ailleurs, mais restaurables depuis la gestion des catégories. */
  readonly archivedCategories: readonly Category[]
  /**
   * Adresse à laquelle écrire pour déposer une fiche dans cet espace. Nulle quand l'espace n'a
   * pas d'alias ou que la relève est coupée — on n'annonce pas une adresse sans effet.
   */
  readonly depositAddress: string | null
  /**
   * Vrai quand on regarde cet espace sans en être membre, parce qu'on l'administre.
   *
   * L'écran le dit alors franchement : un accès d'administration silencieux ferait oublier où
   * l'on est, et le propriétaire ne pourrait pas s'y attendre.
   */
  readonly viewerIsAdminGuest: boolean
  /** Les membres de l'espace : ils se connaissent, et il faut pouvoir désigner un destinataire. */
  readonly members: readonly SpaceMemberSummary[]
  /** Ce que chaque outil a à annoncer aujourd'hui. */
  readonly tools: SpaceToolCounts
}

export type SpaceToolCounts = {
  readonly agendaToday: number
  readonly shoppingRemaining: number
  readonly giftsPending: number
  readonly photos: number
  /** Solde en cours, en centimes. Zéro quand tout est à jour. */
  readonly balanceCents: number
}

export type ExpenseShare = {
  readonly participantId: string
  readonly displayName: string
  readonly amountCents: number
}

/**
 * Quelqu'un qui partage des dépenses : un membre de l'espace, ou un invité.
 *
 * Un invité n'a qu'un prénom — pas de compte, pas de connexion, rien à voir de l'espace. Il
 * compte dans les parts et dans le solde, et c'est tout.
 */
export type Participant = {
  readonly id: string
  readonly displayName: string
  readonly isGuest: boolean
  /** Écarté des prochaines dépenses ; ses parts passées restent. */
  readonly isRemoved: boolean
}

export type Expense = {
  readonly id: string
  readonly label: string
  readonly amountCents: number
  readonly paidByParticipantId: string
  readonly paidByName: string
  readonly categoryId: string | null
  readonly categoryName: string | null
  readonly categoryInitials: string | null
  readonly categoryHue: number | null
  readonly splitMode: 'equal' | 'shares' | 'amounts'
  readonly occurredOn: string
  /** Fiche d'origine, quand la dépense vient d'une idée rangée dans l'espace. */
  readonly itemId: string | null
  readonly itemTitle: string | null
  /** Un remboursement, pas une dépense : de l'argent rendu n'est pas de l'argent dépensé. */
  readonly isRepayment: boolean
  readonly shares: readonly ExpenseShare[]
}

export type BalanceLine = {
  readonly participantId: string
  readonly displayName: string
  /** Positif : on lui doit. Négatif : il doit. */
  readonly netCents: number
  /** Ce qu'il a avancé. */
  readonly paidCents: number
  /** Ce qui lui revient à charge. */
  readonly owedCents: number
}

/** Un virement à faire pour solder. */
export type Transfert = {
  readonly fromParticipantId: string
  readonly fromName: string
  readonly toParticipantId: string
  readonly toName: string
  readonly amountCents: number
  readonly sentence: string
}

export type Balance = {
  /** Le solde en une phrase — un tableau de soldes croisés ne se lit pas. */
  readonly sentence: string
  readonly fromParticipantId: string | null
  readonly toParticipantId: string | null
  readonly amountCents: number
  readonly lines: readonly BalanceLine[]
  /**
   * Les virements à faire pour que tout le monde retombe à zéro. Vide quand tout est à jour.
   * À deux, la phrase suffisait ; à cinq, c'est cette liste qui dit quoi faire.
   */
  readonly transfers: readonly Transfert[]
}

export type ExpensesResponse = {
  readonly expenses: readonly Expense[]
  readonly balance: Balance
  /** Total dépensé sur la période, remboursements exclus. */
  readonly totalCents: number
  /** Qui peut prendre part aux dépenses : membres et invités. */
  readonly participants: readonly Participant[]
}

/** Une période soldée : ce que « Marquer comme réglé » a archivé. */
export type Settlement = {
  readonly id: string
  readonly settledAt: string
  readonly sentence: string
  readonly amountCents: number
  /** Ce que la période totalisait, remboursements exclus. */
  readonly totalCents: number
  readonly expenseCount: number
  readonly expenses: readonly Expense[]
}

export type GalleryImage = {
  readonly id: string
  readonly url: string
  readonly width: number
  readonly height: number
  /** Fiche d'origine : c'est vers elle qu'on retourne en touchant l'image. */
  readonly itemId: string
  readonly itemTitle: string
  readonly createdAt: string
}

export type GalleryPage = {
  readonly images: readonly GalleryImage[]
  readonly nextCursor: string | null
}

export type SpaceMemberSummary = {
  readonly userId: string
  readonly displayName: string
}

/** Nature d'un bloc de fiche. */
export type BlockKind = 'text' | 'checklist' | 'image' | 'link'

export type ItemBlockLine = {
  readonly id: string
  readonly label: string
  readonly isChecked: boolean
}

/** Taille du texte d'un bloc, prise dans l'échelle typographique du cahier. */
export type BlockTextSize = 'petit' | 'normal' | 'grand'

/**
 * Teinte du texte d'un bloc.
 *
 * Liste fermée : chaque ton a deux valeurs côté feuille de style, une par thème. Aucune teinte
 * unique ne se lit à la fois sur une carte blanche et sur une carte sombre.
 */
export type BlockTextColor = 'rouge' | 'vert' | 'bleu' | 'violet' | 'ambre'

export type ItemBlock = {
  readonly id: string
  readonly kind: BlockKind
  /** Titre donné à la main. Nul : la nature du bloc s'affiche à la place. */
  readonly title: string | null
  readonly textSize: BlockTextSize | null
  readonly textColor: BlockTextColor | null
  readonly text: string | null
  readonly url: string | null
  readonly attachmentId: string | null
  readonly imageUrl: string | null
  readonly lines: readonly ItemBlockLine[]
}

export type Item = {
  readonly id: string
  readonly type: 'link' | 'note' | 'photo' | 'shopping'
  readonly title: string
  readonly description: string | null
  readonly url: string | null
  readonly domain: string | null
  readonly imageUrl: string | null
  readonly categoryId: string | null
  readonly categoryName: string | null
  readonly categoryInitials: string | null
  readonly categoryHue: number | null
  readonly status: 'unsorted' | 'sorted' | 'archived'
  readonly isFavorite: boolean
  /** Coché dans une catégorie en liste à cocher. État distinct de l'archivage. */
  readonly isDone: boolean
  readonly createdByName: string
  readonly createdAt: string
  readonly images: readonly ItemImage[]
  /** Les blocs qui composent la fiche, dans l'ordre. Vide : la fiche est telle qu'avant. */
  readonly blocks: readonly ItemBlock[]
  /** Rendez-vous créés depuis cette fiche, pour pouvoir les retirer d'ici. */
  readonly events: readonly LinkedEvent[]
  /** La fiche est cachée à quelqu'un de l'espace : c'est un cadeau en préparation. */
  readonly hiddenFromSomeone: boolean
  /** Sous verrou : le contenu n'arrive qu'après le code, et le serveur ne l'envoie pas avant. */
  readonly isLocked: boolean
  /** Qui l'a verrouillée, pour le dire à ceux qui n'y ont pas droit. */
  readonly lockedByName: string | null
  /**
   * C'est moi qui l'ai verrouillée. Distinct de `canOpen` : celui à qui on a confié l'accès
   * ouvre la fiche, mais ne retire pas le verrou et ne le confie pas à son tour.
   */
  readonly lockedByMe: boolean
  /** J'ai le droit d'ouvrir — reste mon code à saisir. Faux : la fiche ne me regarde pas. */
  readonly canOpen: boolean
}

/** Qui peut ouvrir une fiche verrouillée, en plus de celui qui a posé le verrou. */
export type LockGrant = {
  readonly userId: string
  readonly name: string
}

export type LinkedEvent = {
  readonly id: string
  readonly title: string
  readonly startsAt: string
  readonly isAllDay: boolean
}

export type Gift = {
  readonly id: string
  readonly title: string
  readonly recipientUserId: string | null
  readonly recipientName: string | null
  /** Masqué à son destinataire : le serveur ne le lui envoie pas du tout. */
  readonly hiddenFromRecipient: boolean
  readonly estimatedPriceCents: number | null
  readonly occasion: string | null
  readonly status: 'idea' | 'bought' | 'given'
  readonly givenAt: string | null
  readonly itemId: string | null
  readonly itemTitle: string | null
  readonly createdByName: string
  readonly createdAt: string
}

export type NewsItem = {
  /** « item » ou « event » : les deux sortes d'ajouts signalées. */
  readonly kind: 'item' | 'event'
  readonly id: string
  readonly spaceId: string
  readonly spaceName: string
  readonly title: string
  readonly domain: string | null
  readonly categoryName: string | null
  readonly categoryInitials: string | null
  readonly categoryHue: number | null
  readonly createdByName: string
  readonly createdAt: string
}

export type NewsResponse = {
  /** Total réel, qui peut dépasser la liste rendue. */
  readonly count: number
  readonly items: readonly NewsItem[]
  readonly since: string
}

/** Ce qu'un résultat de recherche désigne : c'est lui qui dit vers quel écran il mène. */
export type SearchKind = 'fiche' | 'agenda' | 'courses' | 'depense' | 'cadeau'

export type SearchHit = {
  readonly id: string
  readonly spaceId: string
  readonly spaceName: string
  readonly kind: SearchKind
  /** Une précision propre à la nature du résultat : une date, un montant. */
  readonly subtitle: string | null
  readonly type: string
  readonly title: string
  readonly domain: string | null
  readonly categoryName: string | null
  readonly categoryInitials: string | null
  readonly categoryHue: number | null
  /** La recherche traverse aussi les archives : chaque résultat dit s'il en vient. */
  readonly isArchived: boolean
  readonly createdAt: string
}

export type SearchResponse = {
  readonly hits: readonly SearchHit[]
  /** Vrai quand le plafond a été atteint : il y a d'autres résultats non montrés. */
  readonly truncated: boolean
}

export type ItemImage = {
  readonly id: string
  /** Chemin servi par l'API, jamais l'emplacement du fichier sur le disque. */
  readonly url: string
  readonly width: number
  readonly height: number
  readonly byteSize: number
}

export type TodayItem = {
  readonly id: string
  readonly spaceId: string
  readonly spaceName: string
  readonly type: 'link' | 'note' | 'photo'
  readonly title: string
  readonly domain: string | null
  readonly imageUrl: string | null
  readonly categoryId: string | null
  readonly categoryName: string | null
  readonly categoryInitials: string | null
  readonly categoryHue: number | null
  readonly createdByName: string
  readonly createdAt: string
}

export type RecurrenceFrequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'

/**
 * Un profil d'agenda : « Gil », « Val ». Quatre au plus par espace.
 *
 * La teinte est celle de la palette des catégories (0 à 9) : c'est elle qui colore la case du
 * jour dans la grille du mois.
 */
export type AgendaProfile = {
  readonly id: string
  readonly name: string
  readonly hue: number
  /**
   * Affiché en barre couvrant ses jours, au lieu de colorer chaque case.
   *
   * Une couleur de case dit « ce jour-là, il y a quelque chose » ; une barre dit « ça dure, du 15
   * au 21 ». C'est la bonne forme pour des vacances, la mauvaise pour un rendez-vous d'une heure.
   */
  readonly showAsBar: boolean
}

export type AgendaEvent = {
  /** Identifiant de la **série**. Plusieurs occurrences le partagent. */
  readonly id: string
  /**
   * Instant de cette occurrence dans la série d'origine. C'est la clé qui désigne une occurrence
   * précise — et non sa date affichée, qui peut avoir été déplacée.
   */
  readonly occurrenceStartsAt: string
  readonly title: string
  readonly note: string | null
  readonly startsAt: string
  readonly endsAt: string | null
  readonly isAllDay: boolean
  readonly itemId: string | null
  readonly itemTitle: string | null
  readonly createdByName: string
  readonly isRecurring: boolean
  /** Cette occurrence a été modifiée individuellement. */
  readonly isException: boolean
  readonly frequency: RecurrenceFrequency
  readonly recurrenceInterval: number
  readonly recurrenceUntil: string | null
  /** Profil auquel il se rattache, et la couleur qui en découle. Nul : aucun profil. */
  readonly profileId: string | null
  readonly profileName: string | null
  readonly profileHue: number | null
}

export type TodayEvent = {
  readonly id: string
  readonly spaceId: string
  readonly spaceName: string
  readonly title: string
  readonly note: string | null
  readonly startsAt: string
  readonly endsAt: string | null
  readonly isAllDay: boolean
}

export type TodayShopping = {
  readonly spaceId: string
  readonly spaceName: string
  readonly remaining: number
  readonly inBasket: number
}

/**
 * Rayon d'un article, tel que le magasin les range.
 *
 * Liste fermée côté serveur : elle doit suivre le trajet dans le magasin pour que la liste
 * groupée fasse gagner des pas.
 */
export type AisleCode =
  | 'fruits_legumes'
  | 'boucherie'
  | 'cremerie'
  | 'boulangerie'
  | 'epicerie_salee'
  | 'epicerie_sucree'
  | 'surgeles'
  | 'boissons'
  | 'hygiene'
  | 'entretien'
  | 'bebe_animaux'

/** Une ancienne liste : ce qu'on a sorti du panier un jour donné. */
export type PastShoppingList = {
  readonly clearedAt: string
  readonly lines: readonly {
    readonly label: string
    readonly quantity: string | null
    readonly aisle: AisleCode | null
  }[]
}

export type ShoppingLine = {
  readonly id: string
  readonly label: string
  readonly quantity: string | null
  readonly isChecked: boolean
  readonly sourceItemId: string | null
  readonly sourceItemTitle: string | null
  readonly createdByName: string
  /** Rayon deviné ou choisi. Nul : non classé. */
  readonly aisle: AisleCode | null
}

export type ShoppingList = {
  readonly remaining: readonly ShoppingLine[]
  readonly inBasket: readonly ShoppingLine[]
}

/** Un élément supprimé, et de quoi le reconnaître. */
export type EntreeDeCorbeille = {
  readonly kind: 'fiche' | 'agenda' | 'depense' | 'cadeau'
  readonly id: string
  readonly title: string
  readonly detail: string | null
  readonly deletedAt: string
}

export type Corbeille = {
  readonly entries: readonly EntreeDeCorbeille[]
  /** Au bout de combien de jours la corbeille se vide d'elle-même. */
  readonly purgeAfterDays: number
}

export type Today = {
  /**
   * Les sept jours qui suivent, aujourd'hui exclu. L'écran s'appelle « Aujourd'hui », mais un
   * jour sans rendez-vous n'a rien à dire — et c'est le cas de la plupart.
   */
  readonly upcoming: readonly TodayEvent[]
  readonly agenda: readonly TodayEvent[]
  readonly shopping: readonly TodayShopping[]
  readonly recent: readonly TodayItem[]
  readonly unsortedCount: number
}

export type ItemPage = {
  readonly items: readonly Item[]
  readonly nextCursor: string | null
}

export type LinkPreview = {
  readonly url: string
  readonly domain: string
  readonly title: string
  readonly description: string | null
  readonly imageUrl: string | null
  /** Faux quand la page n'a pas pu être lue : le titre vaut alors le domaine. */
  readonly resolved: boolean
  readonly suggestedCategoryId: string | null
  readonly suggestionReason: string | null
}

export type NewItem = {
  readonly type: 'link' | 'note'
  readonly title?: string
  readonly url?: string
  readonly description?: string
  readonly imageUrl?: string
  readonly categoryId?: string
}

export type Invitation = {
  readonly code: string
  readonly expiresAt: string
}

export const api = {
  requestMagicLink: (email: string) =>
    request<{ resendAfterSeconds: number }>('/api/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  openSession: (token: string) =>
    request<void>('/api/auth/session', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  closeSession: () => request<void>('/api/auth/session', { method: 'DELETE' }),

  me: () => request<Me>('/api/me'),

  createSpace: (name: string) =>
    request<SpaceSummary>('/api/spaces', { method: 'POST', body: JSON.stringify({ name }) }),

  joinSpace: (code: string) =>
    request<SpaceSummary>('/api/spaces/join', { method: 'POST', body: JSON.stringify({ code }) }),

  inviteToSpace: (spaceId: string) =>
    request<Invitation>(`/api/spaces/${spaceId}/invitations`, { method: 'POST' }),

  setActiveSpace: (spaceId: string) =>
    request<void>('/api/me/active-space', { method: 'PUT', body: JSON.stringify({ spaceId }) }),

  space: (spaceId: string) => request<SpaceDetail>(`/api/spaces/${spaceId}`),

  createCategory: (spaceId: string, name: string) =>
    request<Category>(`/api/spaces/${spaceId}/categories`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  /**
   * Range les catégories dans l'ordre donné, de la première à la dernière.
   *
   * La liste entière plutôt qu'un déplacement : un ordre est un tout, et le dernier qui valide
   * impose le sien — sans états intermédiaires si deux personnes réorganisent en même temps.
   */
  /**
   * Renomme l'espace. L'emblème suit le nom — sinon « Voyages » garderait la marque « Fa ».
   *
   * Tout membre peut le faire : un nom n'engage que ceux qui le lisent, et se corrige aussi
   * vite qu'il se change.
   */
  renameSpace: (spaceId: string, name: string) =>
    request<void>(`/api/spaces/${spaceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  reorderCategories: (spaceId: string, ids: readonly string[]) =>
    request<void>(`/api/spaces/${spaceId}/categories/order`, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),

  patchCategory: (
    spaceId: string,
    categoryId: string,
    patch: {
      name?: string
      archived?: boolean
      displayMode?: CategoryDisplayMode
      showIngredients?: boolean
      showGift?: boolean
      showExpense?: boolean
    },
  ) =>
    request<Category>(`/api/spaces/${spaceId}/categories/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  items: (
    spaceId: string,
    params: {
      categoryId?: string
      favorite?: boolean
      /** Recherche sur le titre, le texte et le domaine. */
      q?: string
      /** Omis, les archives ne remontent pas : elles ont leur propre vue. */
      status?: 'unsorted' | 'sorted' | 'archived'
      limit?: number
    } = {},
  ) => {
    const query = new URLSearchParams()
    if (params.categoryId) query.set('categoryId', params.categoryId)
    if (params.favorite) query.set('favorite', 'true')
    if (params.q) query.set('q', params.q)
    if (params.status) query.set('status', params.status)
    if (params.limit) query.set('limit', String(params.limit))
    const suffix = query.size > 0 ? `?${query}` : ''
    return request<ItemPage>(`/api/spaces/${spaceId}/items${suffix}`)
  },

  /**
   * La clé d'idempotence est produite ici, avant l'envoi : c'est elle qui rend le rejeu sûr
   * quand la file hors ligne réémet une requête dont la réponse s'est perdue (décision A5).
   */
  createItem: (spaceId: string, item: NewItem) =>
    request<Item>(`/api/spaces/${spaceId}/items`, {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey: uuidV7(), ...item }),
    }),

  /**
   * « Aujourd'hui », découpé selon le fuseau de l'appareil.
   *
   * Le serveur découpait la journée en UTC : à la Réunion (UTC+4), un rendez-vous « toute la
   * journée » — enregistré à minuit local, soit 20 h 00 la veille en UTC — s'affichait la veille
   * et disparaissait le jour même. Le décalage se transmet donc, avec la convention de
   * `getTimezoneOffset` : minutes en retard sur UTC, -240 pour UTC+4.
   */
  today: () =>
    request<Today>(`/api/today?offsetMinutes=${new Date().getTimezoneOffset()}`),

  passwordLogin: (login: string, password: string) =>
    request<void>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/me/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  adminMail: () => request<MailSettings>('/api/admin/mail'),

  /** Mot de passe omis = mot de passe inchangé ; l'écran ne peut pas le réafficher. */
  adminSaveMail: (settings: {
    address: string
    senderName?: string
    appPassword?: string
    sendEnabled: boolean
    receiveEnabled: boolean
    pollMinutes: number
  }) =>
    request<MailSettings>('/api/admin/mail', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),

  adminMailMessages: () => request<readonly ImportedMessage[]>('/api/admin/mail/messages'),

  adminRelever: () => request<IngestionReport>('/api/admin/mail/relever', { method: 'POST' }),

  /** Range un message écarté dans un espace. La fiche arrive « à classer ». */
  adminRangerMessage: (messageId: string, spaceId: string) =>
    request<ImportedMessage>(`/api/admin/mail/messages/${messageId}/ranger`, {
      method: 'POST',
      body: JSON.stringify({ spaceId }),
    }),

  adminIgnorerMessage: (messageId: string) =>
    request<void>(`/api/admin/mail/messages/${messageId}/ignorer`, { method: 'POST' }),

  /** Déplace le message vers la corbeille de Gmail. */
  adminSupprimerLeMail: (messageId: string) =>
    request<TestMailResult>(`/api/admin/mail/messages/${messageId}/supprimer-le-mail`, {
      method: 'POST',
    }),

  adminTestMail: (to: string) =>
    request<TestMailResult>('/api/admin/mail/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    }),

  adminUsers: () => request<readonly AdminUser[]>('/api/admin/users'),

  adminSpaces: () => request<readonly AdminSpace[]>('/api/admin/spaces'),

  adminSpace: (spaceId: string) => request<AdminSpaceDetail>(`/api/admin/spaces/${spaceId}`),

  adminRemoveMember: (spaceId: string, userId: string) =>
    request<void>(`/api/admin/spaces/${spaceId}/members/${userId}`, { method: 'DELETE' }),

  /** Sans mot de passe fourni, le serveur en engendre un et le rend une seule fois. */
  adminCreateUser: (compte: {
    email: string
    firstName?: string
    lastName?: string
    password?: string
  }) =>
    request<CreatedUser>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(compte),
    }),

  /** Chacun décide de la façon dont on le nomme. */
  renameMe: (displayName: string) =>
    request<void>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    }),

  adminSetPassword: (userId: string, newPassword: string) =>
    request<void>(`/api/admin/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),

  adminCreateSpace: (name: string, ownerUserId: string) =>
    request<AdminSpace>('/api/admin/spaces', {
      method: 'POST',
      body: JSON.stringify({ name, ownerUserId }),
    }),

  adminAddMember: (spaceId: string, userId: string) =>
    request<void>(`/api/admin/spaces/${spaceId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  adminRenameSpace: (spaceId: string, name: string, emailAlias?: string) =>
    request<AdminSpace>(`/api/admin/spaces/${spaceId}`, {
      method: 'PATCH',
      // Alias omis = alias inchangé ; chaîne vide = alias retiré.
      body: JSON.stringify({ name, ...(emailAlias === undefined ? {} : { emailAlias }) }),
    }),

  adminSetAdmin: (userId: string, isAdmin: boolean) =>
    request<void>(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isAdmin }),
    }),

  /**
   * Corrige un compte : son nom, son adresse.
   *
   * L'adresse sert à se connecter et reçoit les courriers : la changer change les deux.
   */
  adminUpdateUser: (userId: string, patch: { displayName?: string; email?: string }) =>
    request<void>(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  adminDeleteUser: (userId: string, confirmation: string) =>
    request<void>(`/api/admin/users/${userId}/delete`, {
      method: 'POST',
      body: JSON.stringify({ confirmation }),
    }),

  adminDeleteSpace: (spaceId: string, confirmation: string) =>
    request<void>(`/api/admin/spaces/${spaceId}/delete`, {
      method: 'POST',
      body: JSON.stringify({ confirmation }),
    }),

  shopping: (spaceId: string) => request<ShoppingList>(`/api/spaces/${spaceId}/shopping`),

  addShoppingLine: (
    spaceId: string,
    label: string,
    quantity?: string,
    sourceItemId?: string,
    aisle?: AisleCode,
  ) =>
    request<ShoppingLine>(`/api/spaces/${spaceId}/shopping`, {
      method: 'POST',
      body: JSON.stringify({ label, quantity, sourceItemId, aisle }),
    }),

  checkShoppingLine: (spaceId: string, lineId: string, isChecked: boolean) =>
    request<void>(`/api/spaces/${spaceId}/shopping/${lineId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isChecked }),
    }),

  /**
   * Range un article dans un rayon — chaîne vide pour le déclasser.
   *
   * Le choix est retenu pour l'espace : le même article y retournera tout seul.
   */
  setShoppingAisle: (spaceId: string, lineId: string, aisle: AisleCode | '') =>
    request<void>(`/api/spaces/${spaceId}/shopping/${lineId}`, {
      method: 'PATCH',
      body: JSON.stringify({ aisle }),
    }),

  /** Les listes déjà faites, de la plus récente à la plus ancienne. */
  shoppingHistory: (spaceId: string) =>
    request<readonly PastShoppingList[]>(`/api/spaces/${spaceId}/shopping/history`),

  deleteShoppingLine: (spaceId: string, lineId: string) =>
    request<void>(`/api/spaces/${spaceId}/shopping/${lineId}`, { method: 'DELETE' }),

  clearBasket: (spaceId: string) =>
    request<number>(`/api/spaces/${spaceId}/shopping/clear-basket`, { method: 'POST' }),

  events: (spaceId: string, days = 5) =>
    request<readonly AgendaEvent[]>(`/api/spaces/${spaceId}/events?days=${days}`),

  /** Fenêtre explicite, pour la grille du mois. Le serveur plafonne à 60 jours. */
  /** Les profils d'agenda d'un espace, quatre au plus. */
  agendaProfiles: (spaceId: string) =>
    request<readonly AgendaProfile[]>(`/api/spaces/${spaceId}/events/profiles`),

  createAgendaProfile: (
    spaceId: string,
    profil: { name: string; hue: number; showAsBar?: boolean },
  ) =>
    request<AgendaProfile>(`/api/spaces/${spaceId}/events/profiles`, {
      method: 'POST',
      body: JSON.stringify(profil),
    }),

  patchAgendaProfile: (
    spaceId: string,
    profileId: string,
    patch: { name?: string; hue?: number; showAsBar?: boolean },
  ) =>
    request<AgendaProfile>(`/api/spaces/${spaceId}/events/profiles/${profileId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Ses rendez-vous restent : ils redeviennent des rendez-vous sans profil. */
  deleteAgendaProfile: (spaceId: string, profileId: string) =>
    request<void>(`/api/spaces/${spaceId}/events/profiles/${profileId}`, { method: 'DELETE' }),

  eventsBetween: (spaceId: string, from: Date, days: number) =>
    request<readonly AgendaEvent[]>(
      `/api/spaces/${spaceId}/events?from=${encodeURIComponent(from.toISOString())}&days=${days}`,
    ),

  /**
   * Remplacement complet des champs modifiables : une fin omise l'efface. C'est le seul moyen
   * de retirer une date de fin une fois posée.
   */
  /**
   * `scope: 'one'` ne touche que l'occurrence désignée par `occurrence` ; `'all'` corrige la
   * série et efface les exceptions qui la contredisaient.
   */
  updateEvent: (
    spaceId: string,
    eventId: string,
    event: {
      title: string
      note?: string
      startsAt: string
      endsAt?: string
      isAllDay: boolean
      frequency?: RecurrenceFrequency
      recurrenceInterval?: number
      recurrenceUntil?: string
      occurrence?: string
      scope?: 'one' | 'all'
      profileId?: string | null
    },
  ) =>
    request<AgendaEvent>(`/api/spaces/${spaceId}/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(event),
    }),

  createEvent: (
    spaceId: string,
    body: {
      title: string
      note?: string
      startsAt: string
      endsAt?: string
      isAllDay: boolean
      itemId?: string
      frequency?: RecurrenceFrequency
      recurrenceInterval?: number
      recurrenceUntil?: string
      profileId?: string | null
    },
  ) =>
    request<AgendaEvent>(`/api/spaces/${spaceId}/events`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Sans `occurrence`, la série entière est retirée. Avec, seule cette occurrence l'est. */
  deleteEvent: (spaceId: string, eventId: string, occurrence?: string) => {
    const suffixe = occurrence
      ? `?scope=one&occurrence=${encodeURIComponent(occurrence)}`
      : ''

    return request<void>(`/api/spaces/${spaceId}/events/${eventId}${suffixe}`, {
      method: 'DELETE',
    })
  },

  item: (spaceId: string, itemId: string) =>
    request<Item>(`/api/spaces/${spaceId}/items/${itemId}`),

  /**
   * Champ absent = champ inchangé. Envoyer `description: ''` efface le texte ; `undefined` le
   * laisse tel quel. Le titre ne peut pas être vidé, le serveur le refuse.
   */
  patchItem: (
    spaceId: string,
    itemId: string,
    patch: {
      categoryId?: string | null
      isFavorite?: boolean
      isDone?: boolean
      title?: string
      description?: string
    },
  ) =>
    request<Item>(`/api/spaces/${spaceId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /**
   * Envoie une image. `FormData` et non JSON : encoder une photo en base64 gonflerait le corps
   * d'un tiers, et le type de contenu doit rester multipart pour que le serveur la reçoive en
   * flux plutôt qu'en mémoire.
   */
  uploadImage: async (spaceId: string, itemId: string, fichier: File) => {
    const corps = new FormData()
    corps.append('fichier', fichier)

    const reponse = await fetch(`/api/spaces/${spaceId}/items/${itemId}/images`, {
      method: 'POST',
      body: corps,
      credentials: 'same-origin',
    })

    if (!reponse.ok) {
      // Le refus du serveur est rendu tel quel : « L'image dépasse 15 Mo », « Ce fichier n'est
      // pas une image reconnue ». C'est ce qui permet de corriger sans deviner.
      const texte = await reponse.text()
      throw new ApiError(reponse.status, texte || "L'envoi de l'image a échoué.")
    }

    return (await reponse.json()) as ItemImage
  },

  /** Ne vaut que pour une catégorie déjà archivée : on n'efface que ce qu'on a rangé. */
  deleteCategory: (spaceId: string, categoryId: string) =>
    request<void>(`/api/spaces/${spaceId}/categories/${categoryId}`, { method: 'DELETE' }),

  deleteImage: (spaceId: string, itemId: string, imageId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/images/${imageId}`, {
      method: 'DELETE',
    }),

  /**
   * Ingrédients proposés pour une fiche. Lus dans les données structurées de la page quand il
   * s'agit d'un lien, sinon dans les lignes du texte.
   */
  ingredients: (spaceId: string, itemId: string) =>
    request<{ readonly ingredients: readonly string[]; readonly source: 'page' | 'note' }>(
      `/api/spaces/${spaceId}/items/${itemId}/ingredients`,
    ),

  expenses: (spaceId: string) => request<ExpensesResponse>(`/api/spaces/${spaceId}/expenses`),

  createExpense: (
    spaceId: string,
    depense: {
      label: string
      amountCents: number
      paidByParticipantId?: string
      categoryId?: string
      splitMode?: 'equal' | 'shares' | 'amounts'
      occurredOn?: string
      /** Rattache la dépense à une fiche — et, si la fiche est un cadeau, à son secret. */
      itemId?: string
      shares?: readonly { participantId: string; weight?: number; amountCents?: number }[]
    },
  ) =>
    request<Expense>(`/api/spaces/${spaceId}/expenses`, {
      method: 'POST',
      body: JSON.stringify(depense),
    }),

  /**
   * Transforme une note contenant une adresse en véritable fiche lien.
   *
   * Le titre et le texte écrits à la main sont conservés : seuls le domaine, l'adresse et
   * l'image d'aperçu sont ajoutés.
   */
  enFaireUnLien: (spaceId: string, itemId: string) =>
    request<Item>(`/api/spaces/${spaceId}/items/${itemId}/lien`, { method: 'POST' }),

  /** Les périodes déjà réglées, de la plus récente à la plus ancienne. */
  settlements: (spaceId: string) =>
    request<readonly Settlement[]>(`/api/spaces/${spaceId}/expenses/settlements`),

  /** Ajoute un bloc à une fiche. Rend son identifiant. */
  addBlock: (
    spaceId: string,
    itemId: string,
    bloc: { kind: BlockKind; text?: string; url?: string; attachmentId?: string },
  ) =>
    request<string>(`/api/spaces/${spaceId}/items/${itemId}/blocks`, {
      method: 'POST',
      body: JSON.stringify(bloc),
    }),

  /** Remplace le contenu d'un bloc : un champ absent est effacé. */
  updateBlock: (
    spaceId: string,
    itemId: string,
    blockId: string,
    bloc: {
      text?: string | null
      url?: string | null
      attachmentId?: string | null
      title?: string | null
      textSize?: BlockTextSize | null
      textColor?: BlockTextColor | null
    },
  ) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/blocks/${blockId}`, {
      method: 'PUT',
      body: JSON.stringify(bloc),
    }),

  deleteBlock: (spaceId: string, itemId: string, blockId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/blocks/${blockId}`, { method: 'DELETE' }),

  reorderBlocks: (spaceId: string, itemId: string, ids: readonly string[]) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/blocks/order`, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),

  addBlockLine: (spaceId: string, itemId: string, blockId: string, label: string) =>
    request<string>(`/api/spaces/${spaceId}/items/${itemId}/blocks/${blockId}/lines`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),

  patchBlockLine: (
    spaceId: string,
    itemId: string,
    blockId: string,
    lineId: string,
    patch: { label?: string; isChecked?: boolean },
  ) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/blocks/${blockId}/lines/${lineId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteBlockLine: (spaceId: string, itemId: string, blockId: string, lineId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/blocks/${blockId}/lines/${lineId}`, {
      method: 'DELETE',
    }),

  /** Les dépenses rattachées à une fiche : la fiche les affiche sous son texte. */
  itemExpenses: (spaceId: string, itemId: string) =>
    request<readonly Expense[]>(`/api/spaces/${spaceId}/expenses/item/${itemId}`),

  updateExpense: (
    spaceId: string,
    expenseId: string,
    depense: {
      label: string
      amountCents: number
      paidByParticipantId?: string
      categoryId?: string
      splitMode?: 'equal' | 'shares' | 'amounts'
      occurredOn?: string
      itemId?: string
      shares?: readonly { participantId: string; weight?: number; amountCents?: number }[]
    },
  ) =>
    request<Expense>(`/api/spaces/${spaceId}/expenses/${expenseId}`, {
      method: 'PUT',
      body: JSON.stringify(depense),
    }),

  /** « J'ai rendu 20 € » : un remboursement partiel, sans solder la période. */
  repayExpense: (
    spaceId: string,
    remboursement: { fromParticipantId: string; toParticipantId: string; amountCents: number },
  ) =>
    request<Expense>(`/api/spaces/${spaceId}/expenses/repayments`, {
      method: 'POST',
      body: JSON.stringify(remboursement),
    }),

  deleteExpense: (spaceId: string, expenseId: string) =>
    request<void>(`/api/spaces/${spaceId}/expenses/${expenseId}`, { method: 'DELETE' }),

  /** Ajoute un invité : un prénom, aucun compte. */
  inviteParticipant: (spaceId: string, displayName: string) =>
    request<Participant>(`/api/spaces/${spaceId}/expenses/participants`, {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    }),

  /** Renomme un invité, l'écarte des prochaines dépenses ou le réintègre. */
  patchParticipant: (
    spaceId: string,
    participantId: string,
    patch: { displayName?: string; removed?: boolean },
  ) =>
    request<Participant>(`/api/spaces/${spaceId}/expenses/participants/${participantId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Archive la période et remet le solde à zéro. Les dépenses ne sont pas effacées. */
  settleExpenses: (spaceId: string) =>
    request<{ readonly message: string; readonly amountCents: number }>(
      `/api/spaces/${spaceId}/expenses/settle`,
      { method: 'POST' },
    ),

  gallery: (spaceId: string, cursor?: string) =>
    request<GalleryPage>(
      `/api/spaces/${spaceId}/gallery${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  gifts: (spaceId: string) => request<readonly Gift[]>(`/api/spaces/${spaceId}/gifts`),

  createGift: (
    spaceId: string,
    gift: {
      title: string
      recipientUserId?: string
      estimatedPriceCents?: number
      occasion?: string
      itemId?: string
    },
  ) =>
    request<Gift>(`/api/spaces/${spaceId}/gifts`, {
      method: 'POST',
      body: JSON.stringify(gift),
    }),

  patchGift: (
    spaceId: string,
    giftId: string,
    patch: {
      title?: string
      recipientUserId?: string
      hiddenFromRecipient?: boolean
      estimatedPriceCents?: number
      occasion?: string
      status?: 'idea' | 'bought' | 'given'
    },
  ) =>
    request<Gift>(`/api/spaces/${spaceId}/gifts/${giftId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteGift: (spaceId: string, giftId: string) =>
    request<void>(`/api/spaces/${spaceId}/gifts/${giftId}`, { method: 'DELETE' }),

  /** Ce que les autres ont ajouté depuis la dernière consultation. */
  news: () => request<NewsResponse>('/api/nouveautes'),

  markNewsSeen: () => request<void>('/api/nouveautes/vu', { method: 'POST' }),

  /** Recherche dans tous les espaces de l'utilisateur, archives comprises. */
  search: (q: string) => request<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`),

  restoreItem: (spaceId: string, itemId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/restore`, { method: 'POST' }),

  archiveItem: (spaceId: string, itemId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/archive`, { method: 'POST' }),

  linkPreview: (spaceId: string, url: string) =>
    request<LinkPreview>(`/api/spaces/${spaceId}/link-preview`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),

  deleteItem: (spaceId: string, itemId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}`, { method: 'DELETE' }),

  /**
   * Définit le code confidentiel, ou le change.
   *
   * `currentPin` est exigé dès qu'un code existe : une session restée ouverte sur un téléphone
   * ne doit pas suffire à s'approprier les fiches qu'elle protège.
   */
  /** La clé publique VAPID. Nulle quand le serveur n'a pas de clés : l'écran n'offre alors rien. */
  trash: (spaceId: string) => request<Corbeille>(`/api/spaces/${spaceId}/trash`),

  restoreFromTrash: (spaceId: string, kind: string, id: string) =>
    request<void>(`/api/spaces/${spaceId}/trash/${kind}/${id}/restore`, { method: 'POST' }),

  pushKey: () => request<{ publicKey: string | null }>('/api/push/key'),

  subscribePush: (abonnement: {
    endpoint: string
    p256dh: string
    auth: string
    offsetMinutes: number
  }) =>
    request<void>('/api/push/subscriptions', {
      method: 'POST',
      body: JSON.stringify(abonnement),
    }),

  /** Sans adresse, coupe les notifications sur **tous** les appareils de la personne. */
  unsubscribePush: (endpoint?: string) =>
    request<void>(
      `/api/push/subscriptions${endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : ''}`,
      { method: 'DELETE' },
    ),

  setPin: (pin: string, currentPin?: string) =>
    request<void>('/api/me/pin', {
      method: 'POST',
      body: JSON.stringify({ pin, ...(currentPin ? { currentPin } : {}) }),
    }),

  lockItem: (spaceId: string, itemId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/lock`, { method: 'POST' }),

  /**
   * Ouvre la fiche pour cette fois et rend son contenu.
   *
   * Rien n'est retenu : le contenu rendu ici ne va pas dans le cache des requêtes, sinon
   * revenir sur la fiche la retrouverait ouverte. Sortir de la fiche doit redemander le code.
   */
  openItem: (spaceId: string, itemId: string, pin: string) =>
    request<Item>(`/api/spaces/${spaceId}/items/${itemId}/lock/open`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),

  unlockItem: (spaceId: string, itemId: string, pin: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/lock/remove`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),

  lockGrants: (spaceId: string, itemId: string) =>
    request<readonly LockGrant[]>(`/api/spaces/${spaceId}/items/${itemId}/lock/grants`),

  grantLock: (spaceId: string, itemId: string, userId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/lock/grants`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  revokeLock: (spaceId: string, itemId: string, userId: string) =>
    request<void>(`/api/spaces/${spaceId}/items/${itemId}/lock/grants/${userId}`, {
      method: 'DELETE',
    }),
}
